import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    addTentativeOrderApiCall,
    createCCAvenuePaymentGatewayApiCall,
    createOrderApiCall,
    createOrderForStripeApiCall,
    getInvoiceApiCall,
    getOrderDetailsApiCall,
} from '@/services/api';
import {
    encryptText
    , objectToQueryString
    , convertObjectToQueryString
} from "@/utils/encryptDecryptUtil";

import { motion } from 'framer-motion';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import SheetButton from '@/components/nextCommonComponents/SheetButton';
import SafeImage from '@/components/nextCommonComponents/SafeImage';
import StripeCheckoutForm from "./StripeCheckoutForm";
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';
import { RootState } from '@/store';

import DarkGreyDownload from "@/assets/icons/darkGreyDownload.png"
import DarkBlueTick from "@/assets/icons/darkBlueTick.png"
import PdfIcon from "@/assets/icons/pdf.png"
import { generateInvoiceFileName, oldPaymentGatewaysToNewOne } from '@/utils';


//@ts-ignore
const PaymentSuccess = ({ orderId, details, onContinue, downloadInvoice, isPdfBtnLoading }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-[10px] p-4 pt-6 text-center flex flex-col items-center border border-black-100 h-full justify-center gap-3"
    >
        
        <div className='flex items-center w-[95px]'>
            <SafeImage
                className='w-[95px]'
                src={DarkBlueTick} height={100} width={95} alt="Success icon" />
        </div>
        <p className="text-[25px] font-[500] text-[#1A73E9] my-4">₹{details.amountPaid}/-</p>

        {details.pdfFileName && (
            <div
                className="w-full max-w-md cursor-pointer"
                onClick={() => downloadInvoice(orderId, details)}
            >                
                <div className="flex items-center justify-between bg-[#F5F3F3] rounded-[5px] p-[5px]">
                    <div className="flex items-center overflow-hidden">
                        <div className="w-[28px] flex-shrink-0">
                            <SafeImage
                                src={PdfIcon}
                                height={28}
                                width={28}
                                alt="PDF icon"
                            />
                        </div>
                        <div className="ml-3 text-left flex-1 overflow-hidden">
                            {/* truncate on overflow */}
                            <p className="text-[15px] font-[500] text-[#324054] truncate">
                                {details.pdfFileName}
                            </p>
                            <p className="text-[15px] font-[400] text-[#71839B]">
                                {details.pdfFileDate}
                            </p>
                        </div>
                    </div>
                    <button className="p-3 hover:bg-gray-100 rounded-full">
                        <div className="w-[25px]">
                            <SafeImage
                                style={{
                                    cursor: isPdfBtnLoading ? 'not-allowed' : 'pointer',
                                    opacity: isPdfBtnLoading ? 0.5 : 1,
                                }}
                                src={DarkGreyDownload}
                                height={21}
                                width={21}
                                alt="Download icon"
                            />
                        </div>
                    </button>
                </div>
            </div>
        )}

        {/* {details.pdfFileName && (
            <div
                className="w-full max-w-md cursor-pointer"
                onClick={() => downloadInvoice(orderId, details)}
            >
                <div className="flex items-center justify-between bg-[#F5F3F3] rounded-[5px] p-[5px]">
                    <div className="flex items-center">
                        <div className='w-[32px]'>
                            <SafeImage
                                src={PdfIcon} height={28} width={28} alt="PDF icon" />
                        </div>
                        <div className="ml-3 text-left">
                            <p className="text-[15px] font-[500] text-[#324054]">{details.pdfFileName}</p>
                            <p className="text-[15px] font-[400] text-[#71839B]">{details.pdfFileDate}</p>
                        </div>
                    </div>
                    <button className="p-3 hover:bg-gray-100 rounded-full">
                        <div className='w-[28px]'>
                            <SafeImage
                                style={{
                                    cursor: isPdfBtnLoading ? 'not-allowed' : 'pointer',
                                    opacity: isPdfBtnLoading ? '0.5' : '1',
                                }}
                                src={DarkGreyDownload} height={21} width={21} alt="Download icon" />
                        </div>
                    </button>
                </div>
            </div>
        )} */}

        <div className="mt-8 w-full text-left text-[14px] font-[400]">
            <div className="flex justify-between mt-3">
                <span className="font-[500]">Transaction Date</span>
                <span className="text-[#606060]">{details.transactionDate}</span>
            </div>
            <div className="flex justify-between mt-3">
                <span className="font-[500]">Transaction ID</span>
                <span className="text-[#606060] break-all text-right">{details.transactionId}</span>
            </div>
            <div className="flex justify-between mt-3">
                <span className="font-[500]">Payment Gateway</span>
                <span className="text-[#606060] capitalize">{details.paymentGatewayName}</span>
            </div>
        </div>

        <SheetButton text="Continue to Dashboard →" onClick={onContinue} className="mt-auto w-full" />
    </motion.div>
);

//@ts-ignore
const PaymentFailed = ({ errorMessage, onRetry, onBack }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-[10px] p-4 pt-6 text-center flex flex-col items-center border border-black-100 h-full justify-center gap-3"
    >
        <div className='flex items-center w-[95px]'>
            <SafeImage
                className='w-[95px]'
                src={DarkBlueTick} height={100} width={95} alt="Error icon" />
        </div>
        <p className="text-[25px] font-[500] text-red-500 my-4">Payment Failed</p>

        <div className="mt-8 w-full text-left text-[14px] font-[400]">
            <div className="flex justify-between mt-3">
                <span className="font-[500]">Error Message</span>
                <span className="text-[#606060]">{errorMessage}</span>
            </div>
        </div>

        <div className="mt-auto w-full space-y-3">
            <SheetButton text="Try Again" onClick={onRetry} className="w-full" />
            <SheetButton text="Back to Home" onClick={onBack} className="w-full bg-gray-200 text-gray-800 hover:bg-gray-300" />
        </div>
    </motion.div>
);

export default function PaymentPage() {
    const navigate = useNavigate();
    const location = useLocation();

    const tentativeOrder = location.state?.tentativeOrder;
    // Map legacy gateways (razorpay/eazypay/axis) → idfc for create-order, handoff, keys, and tentative order
    const paymentMethod = oldPaymentGatewaysToNewOne(location.state?.paymentGateway);
    // const paymentMethod: any = "stripe"; // testing : razorpay | stripe | eazypay

    const timelineDetails = useSelector((state: RootState) => state.timelineReducer.timeline);

    const dataFetchedRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loaderNote, setLoaderNote] = useState('');
    const [status, setStatus] = useState('initial');
    const [details, setDetails] = useState<any>(null);
    const [errorMsg, setErrorMsg] = useState<any>(null);
    const [currentOrderId, setCurrentOrderId] = useState(null);
    const orderIdRef = useRef(null);
    const [stripePromise, setStripePromise] = useState(null);
    const [clientSecret, setClientSecret] = useState(null);
    const [loadingDetails, setLoadingDetails] = useState(false);
    const [isPdfBtnLoading, setIsPdfBtnLoading] = useState(false);
    const IFRAME_HANDOFF_MESSAGE = 'EMEDISKILL_IFRAME_HANDOFF';

    useEffect(() => {
        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.async = true;
        script.id = "razorpay-script";
        document.body.appendChild(script);

        return () => {
            const existing = document.getElementById("razorpay-script");
            if (existing) existing.remove();
        };
    }, []);

    useEffect(() => {
        window.scroll(0, 0);
        if (!tentativeOrder && status === 'initial') {
            navigate('/');
            return;
        }
        // debugger
        if (status === 'initial' && tentativeOrder && paymentMethod && !dataFetchedRef.current) {
            dataFetchedRef.current = true;
            setIsLoading(true);
            setStatus('processing');
            setLoaderNote('Initializing payment gateway...');
            checkLogin();
        }
    }, [tentativeOrder, paymentMethod, status, navigate]);


    useEffect(() => {
        orderIdRef.current = currentOrderId;
    }, [currentOrderId]);

    const checkLogin = () => {
        if (paymentMethod === 'stripe') generateStripeOrderId();
        else if (['razorpay', 'eazypay', 'idfc', 'axis'].includes(paymentMethod)) {
            const postedToParent = handoffCCAvenueToParent();
            if (!postedToParent) generateRazorpayOrderId(oldPaymentGatewaysToNewOne(paymentMethod));
        }
        else if (paymentMethod === 'ccavenue') {
            const postedToParent = handoffCCAvenueToParent();
            if (!postedToParent) generateCCAvenueOrderId();
        }
    };

    const handoffCCAvenueToParent = () => {
        if (!tentativeOrder) return false;
        if (typeof window === 'undefined' || window.parent === window) return false;

        const targetOrigin =  process.env.REACT_APP_USER_PANEL_URL || '*';
        const message = {
            type: IFRAME_HANDOFF_MESSAGE,
            payload: {
                tentativeOrder,
                paymentGateway: oldPaymentGatewaysToNewOne(paymentMethod),
            },
        };
        window.parent.postMessage(
            message,
            targetOrigin
        );
        setLoaderNote('Redirecting payment to parent website...');
        setIsLoading(false);
        return true;
    };

    const handleRetry = () => {
        setStatus('initial');
        setErrorMsg(null);
        setDetails(null);
        dataFetchedRef.current = false;
        setIsLoading(false);
    };

    const handleBack = () => navigate('/');

    const fail = async (msg: any) => {
        setStatus('failed');
        setErrorMsg(msg);
        setIsLoading(false);
    };

    // ─── Order Creation ────────────────────────────────────────────────────────────
    const generateStripeOrderId = async () => {
        try {
            setLoaderNote('Creating Stripe order...');
            const amount = Math.round(tentativeOrder.netTotal);
            const createOrderForStripeApiCallStatus = await createOrderForStripeApiCall({ amount, currency: tentativeOrder.currencyCode });
            if (createOrderForStripeApiCallStatus.status && createOrderForStripeApiCallStatus.data) {
                const data = createOrderForStripeApiCallStatus.data
                await postTentativeOrder(data.id, data);
            } else {
                fail('Failed to create Stripe order')
            };
        } catch (e) { fail('Stripe order creation error.'); }
    };

    const generateRazorpayOrderId = async (gateway: any) => {
        try {
            setLoaderNote('Creating payment order...');
            // debugger
            const createOrderApiCallStatus = await createOrderApiCall({
                paymentGateway: gateway,
                amount: tentativeOrder.grandTotal,
                currency: tentativeOrder.currencyCode
            });
            if (createOrderApiCallStatus.status && createOrderApiCallStatus.data) {
                const data = createOrderApiCallStatus.data
                await postTentativeOrder(data.id, data);
            } else {
                fail('Failed to create order')
            };
        } catch (e) { fail('Order creation error.'); }
    };

    const generateCCAvenueOrderId = async () => {
        try {
            const id = Math.floor(Math.random() * 1e9);
            await postTentativeOrder(id, { id });
        } catch (e) { console.error(e); }
    };

    const postTentativeOrder = async (orderId: any, rawDetails: any) => {
        try {
            setLoaderNote('Processing payment details...');
            const orderPayload = {
                ...tentativeOrder,
                courses: timelineDetails.orderDetails.courses.map((course: any) => {
                    return {
                        courseId: course.courseId,
                        title: course.title,
                        mnemonic: course.mnemonic,
                        rectangleThumbnailUrl: course.rectangleThumbnailUrl,
                        squareThumbnailUrl: course.squareThumbnailUrl,
                        pricingPlan: course.pricingPlan,
                        isInOffer: (() => {
                            const currentDate = new Date();
                            const startDate = new Date(course.offerStartAt);
                            const endDate = new Date(course.offerEndAt);
                            return currentDate >= startDate && currentDate <= endDate;
                        })(),
                        offerStartAt: course.offerStartAt,
                        offerEndAt: course.offerEndAt,
                        offerRefreshAt: course.offerRefreshAt,
                        categoryId: course.categoryId
                    }
                }),

                orderId: orderId,
                transactionId: orderId,
                // transactionId: (paymentMethod === 'stripe' || paymentMethod === 'ccavenue')
                //     ? rawDetails.id : `pay_${rawDetails.id}`,

                paymentMethod: null,
                paymentGateway: paymentMethod,
            };
            // debugger

            const qs = objectToQueryString(orderPayload);
            const encrypted = encryptText(qs);

            const { status: ok, data, message } = await addTentativeOrderApiCall({ order: encrypted });
            // debugger
            if (!ok) return fail(message || 'Tentative order failed');
            setCurrentOrderId(data.orderId);

            // launch proper checkout
            if (paymentMethod === 'stripe') return finalizeStripe(rawDetails);
            if (paymentMethod === 'ccavenue') return finalizeCCAvenue(rawDetails, data);
            return finalizeRazorpay(rawDetails);
        } catch (e) { fail('Adding tentative order error.'); }
    };

    // ─── Checkout Handlers ─────────────────────────────────────────────────────────
    const finalizeRazorpay = (details: any) => {
        //@ts-ignore
        if (!window.Razorpay) return fail('Razorpay not loaded');
        setIsLoading(false);
        setLoaderNote('Opening payment window...');

        //@ts-ignore
        const rzp = new window.Razorpay({
            key: process.env.REACT_APP_PUBLIC_IDFC_API_KEY,

            amount: details.amount,
            currency: details.currency,
            code: timelineDetails.orderDetails.currencyCode,
            name: "eMediSkill Services LLP",
            description: "Secure Payment with eMediSkill",
            image: "https://raw.githubusercontent.com/e-MediSkill/eMediSkill-assets/main/rayzorpayIcon.png",
            order_id: details.id,
            handler: (resp: any) => {
                setStatus('success');
                handleSuccess(details.id);
            },
            modal: { ondismiss: () => fail('Payment cancelled by user.') }
        });
        rzp.open();
    };

    const finalizeStripe = (details: any) => {
        const stripePromise: any = loadStripe(process.env.REACT_APP_PUBLIC_STRIPE_PUBLISHABLE_API_KEY || '');
        setStripePromise(stripePromise);
        setClientSecret(details.client_secret);
    };

    const finalizeCCAvenue = async (details: any, tentativeOrder: any) => {
        const payload = {
            tid: details.id * 2,
            order_id: details.id,
            merchant_id: process.env.REACT_APP_CCAVENUE_MERCHANT_ID,
            amount: Math.ceil(tentativeOrder.grandTotal),
            currency: tentativeOrder.currencyCode,
            redirect_url: process.env.REACT_APP_CCAVENUE_REDIRECT_URL,
            cancel_url: process.env.REACT_APP_CCAVENUE_CANCEL_URL,
            language: 'EN'
        };
        const encrypted = encryptText(convertObjectToQueryString(payload));
        // debugger
        const { data: html } = await createCCAvenuePaymentGatewayApiCall({ data: encrypted });
        const div = document.createElement('div');
        // debugger
        div.innerHTML = html;
        document.body.appendChild(div);
        div.querySelector('form')?.submit();
    };

    // ─── After successful payment ──────────────────────────────────────────────────
    const handleSuccess = async (orderId: any) => {
        try {
            setLoadingDetails(true);
            // console.log(orderId)
            // console.log(orderIdRef)
            // debugger
            const getOrderDetailsApiCallStatus: any = await getOrderDetailsApiCall(orderId);
            if (getOrderDetailsApiCallStatus.status
                && getOrderDetailsApiCallStatus.data) {
                const data = getOrderDetailsApiCallStatus.data.data
                // {console.log(generateInvoiceFileName(data))}
                setDetails({
                    amountPaid: data.netTotal,
                    transactionDate: new Date().toLocaleDateString(),
                    transactionId: data.transactionId,
                    paymentGatewayName: paymentMethod,
                    pdfFileName: generateInvoiceFileName(data),
                    pdfFileDate: new Date().toLocaleDateString()
                });
                setStatus('success');
            } else {
                setStatus('failed')
                const msg = getOrderDetailsApiCallStatus.data ? getOrderDetailsApiCallStatus.data.message : 'Unknown error from payment success'
                setErrorMsg(msg);
            };
        } catch {
            setStatus('failed');
            setErrorMsg("Unknown error from payment success");
        } finally {
            setLoadingDetails(false);
        }
    };


    const downloadInvoice = async (orderId: any, details: any) => {
        setIsPdfBtnLoading(true)
        try {
            const response: any = await getInvoiceApiCall(orderId);
            if (response !== null) {
                // 1) wrap the raw bytes in a Blob of type PDF
                const pdfBlob = new Blob([response], { type: 'application/pdf' });
                // 2) turn that Blob into an object URL
                const url = window.URL.createObjectURL(pdfBlob);
                // 3) create a temporary link and click it
                const link = document.createElement('a');
                link.href = url;
                link.download = `invoice_${details.pdfFileName}.pdf`;
                document.body.appendChild(link);
                link.click();
                link.remove();
                // 4) cleanup
                window.URL.revokeObjectURL(url);
            } else {
                // handle fail
            }
        } catch (err) {
            console.error('Error downloading invoice PDF', err);
            // you can show an error toast here
        }
        setIsPdfBtnLoading(false)
    };



    // ─── Render ────────────────────────────────────────────────────────────────────
    if (!tentativeOrder && status === 'initial') {
        return (
            <div className="min-h-screen flex items-center justify-center bg-gray-50">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                <p className="ml-4 text-gray-600">Loading payment details...</p>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex items-center justify-center bg-white p-4">
            <div className="w-full max-w-sm">
                {/* {status} */}
                {status === 'processing' && isLoading ? (
                    clientSecret && paymentMethod === 'stripe' ? (
                        <Elements stripe={stripePromise} options={{ clientSecret }}>
                            <StripeCheckoutForm
                                handlePaymentSuccess={handleSuccess}
                                tentativeOrder={tentativeOrder} />
                        </Elements>
                    ) : (
                        <div className="text-center p-6 bg-white rounded-lg shadow">
                            <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto" />
                            <p className="mt-4 text-gray-600">{loaderNote}</p>
                        </div>
                    )
                ) : status === 'success' && details ? (
                    <PaymentSuccess
                        downloadInvoice={downloadInvoice}
                        isPdfBtnLoading={isPdfBtnLoading}
                        orderId={currentOrderId}
                        details={details}
                        onContinue={() => navigate('/')} />
                ) : status === 'failed' && errorMsg ? (
                    <PaymentFailed errorMessage={errorMsg} onRetry={handleRetry} onBack={handleBack} />
                ) : status === 'initial' ? (
                    <div className="min-h-screen flex items-center justify-center bg-gray-50">
                        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                        <p className="ml-4 text-gray-600">Loading payment details...</p>
                    </div>

                    // <PaymentFailed errorMessage={errorMsg} onRetry={handleRetry} onBack={handleBack} />
                ) : null}
            </div>
        </div>
    );
}


// const downloadInvoice = async (orderId: any, details: any) => {
//     // setIsPdfBtnLoading()
//     try {
//         const response: any = await getInvoiceApiCall(orderId);
//         if (response !== null) {
//             // 1) wrap the raw bytes in a Blob of type PDF
//             const pdfBlob = new Blob([response], { type: 'application/pdf' });
//             // 2) turn that Blob into an object URL
//             const url = window.URL.createObjectURL(pdfBlob);
//             // 3) create a temporary link and click it
//             const link = document.createElement('a');
//             link.href = url;
//             link.download = `invoice_${details.pdfFileName}.pdf`;
//             document.body.appendChild(link);
//             link.click();
//             link.remove();
//             // 4) cleanup
//             window.URL.revokeObjectURL(url);
//         } else{
//             // handle fail
//         }
//     } catch (err) {
//         console.error('Error downloading invoice PDF', err);
//         // you can show an error toast here
//     }
// };
